import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NumericFormat } from 'react-number-format'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'
import { useDebouncedCallback } from 'use-debounce'

import { useGetOrderItemsQuery, useLazyGetOrderItemsQuery, useUpdateOrderItemMutation } from '@/app/services/orderItems'
import { useGetOrderQuery, useUpdateOrderMutation } from '@/app/services/orders'
import { selectLocale } from '@/app/slices/appSlice'
import { addItemToUpdateQueue, removeItemFromUpdateQueue, setSelectedOrderStatus } from '@/app/slices/orderItemsSlice'
import { selectCurrencyCode, selectOrganisationId } from '@/app/slices/organisationSlice'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'
import { getCurrencyFormat } from '@/common/utils'
import { ORDER_STATUS_ARRAY } from '@/common/utils/Constants/Constants'

const classes = {
    formControl_medium: {
        width: '150px',
    },
    formControl_small: {
        width: '100px',
    },
}

const ItemFolding = ({ orderItemId }) => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { orderId } = useParams()
    const dispatch = useDispatch()

    const organisationId = useSelector(selectOrganisationId)
    const locale = useSelector(selectLocale)
    const currencyCode = useSelector(selectCurrencyCode)

    const { data: selectedOrder } = useGetOrderQuery({ organisationId, orderId })
    const { orderItem } = useGetOrderItemsQuery(
        {
            organisationId,
            orderId,
        },
        {
            selectFromResult: ({ data }) => ({
                orderItem: data?.find((item) => item.id === orderItemId),
            }),
        }
    )

    const [updateOrder] = useUpdateOrderMutation()
    const [updateOrderItem] = useUpdateOrderItemMutation()
    const [getOrderItems] = useLazyGetOrderItemsQuery()

    const [itemSource, setItemSource] = useState(orderItem?.foldingSource || '')
    const [itemMaxLength, setItemMaxLength] = useState(orderItem?.maxFoldLength || '')
    const [itemNumberOfFolds, setItemNumberOfFolds] = useState(orderItem?.numberOfFolds || '')

    const [maxLengthError, setMaxLengthError] = useState(!itemMaxLength)
    const [numberOfFoldsError, setNumberOfFoldsError] = useState(!itemNumberOfFolds)

    const units = orderItem?.isImperial ? 'in' : 'mm'

    const isPartFromLibrary = Boolean(orderItem?.partLibraryEntryId)

    const currencyFormat = getCurrencyFormat(currencyCode, locale)

    const handleUpdateOrder = async () => {
        try {
            await updateOrder({
                organisationId,
                orderId,
                order: {
                    ...selectedOrder,
                    status: ORDER_STATUS_ARRAY.NotCalculated,
                },
            }).unwrap()
        } catch (_e) {
            const errorMessage = t('$t(An error occurred) updating the order.')
            enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 5000 })
        }
    }

    const handleUpdateOrderItem = async (orderItem) => {
        try {
            await updateOrderItem({
                organisationId,
                orderId,
                orderItemId: orderItem?.id,
                orderItem,
            }).unwrap()

            if (selectedOrder.status !== ORDER_STATUS_ARRAY.NotCalculated) {
                handleUpdateOrder()
            }
            dispatch(removeItemFromUpdateQueue(orderItemId))
        } catch (error) {
            enqueueSnackbar(t('Failed to update the order item'), {
                variant: 'error',
            })
        }
    }

    const debounceHandleUpdateOrderItem = useDebouncedCallback(handleUpdateOrderItem, 1500)

    const handleSourceChange = async (event) => {
        if (selectedOrder.status !== ORDER_STATUS_ARRAY.NotCalculated) {
            dispatch(setSelectedOrderStatus(ORDER_STATUS_ARRAY.NotCalculated))
        }

        const value = event?.target?.value
        if (Boolean(value) && itemSource !== value) {
            setItemSource(value)

            await handleUpdateOrderItem({
                ...orderItem,
                foldingSource: value,
            })

            if (value === 'Drawing') {
                await getOrderItems({ organisationId, orderId })
            }
        }
    }

    const handleMaxLengthChange = async (values) => {
        debounceHandleUpdateOrderItem.cancel()
        dispatch(addItemToUpdateQueue(orderItemId))

        if (selectedOrder.status !== ORDER_STATUS_ARRAY.NotCalculated) {
            dispatch(setSelectedOrderStatus(ORDER_STATUS_ARRAY.NotCalculated))
        }

        const { floatValue } = values
        setMaxLengthError(!floatValue || isNaN(floatValue) || floatValue === 0)
        setItemMaxLength(floatValue)

        if (!isNaN(floatValue) && floatValue !== 0 && itemNumberOfFolds !== 0) {
            debounceHandleUpdateOrderItem({
                ...orderItem,
                maxFoldLength: floatValue,
                numberOfFolds: itemNumberOfFolds,
            })
        }
    }

    const handleNumberOfFoldsChange = async (values) => {
        debounceHandleUpdateOrderItem.cancel()
        dispatch(addItemToUpdateQueue(orderItemId))

        if (selectedOrder.status !== ORDER_STATUS_ARRAY.NotCalculated) {
            dispatch(setSelectedOrderStatus(ORDER_STATUS_ARRAY.NotCalculated))
        }

        const { floatValue } = values
        setNumberOfFoldsError(!floatValue || isNaN(floatValue) || floatValue === 0)
        setItemNumberOfFolds(floatValue)

        if (!isNaN(floatValue) && floatValue !== 0 && itemMaxLength !== 0) {
            debounceHandleUpdateOrderItem({
                ...orderItem,
                numberOfFolds: floatValue,
                maxFoldLength: itemMaxLength,
            })
        }
    }

    return (
        <Box
            sx={{
                alignItems: 'center',
                display: 'flex',
                flex: '1',
                justifyContent: 'space-between',
                width: '100%',
            }}
        >
            <Box>
                <Typography
                    data-testid="order-item-folding-section-title"
                    variant="body2"
                >
                    {t('Folding')}
                </Typography>
            </Box>

            <Box
                display="flex"
                gap={1.5}
            >
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <FormControl
                        color="primary"
                        size="small"
                        sx={classes.formControl_medium}
                    >
                        <InputLabel>{t('Input mode')}</InputLabel>
                        <Select
                            data-testid="order-item-source"
                            disabled={isPartFromLibrary}
                            id="select-itemSource"
                            label={t('Input mode')}
                            labelId="select-itemSource-label"
                            sx={{
                                '& .MuiSelect-select': {
                                    fontSize: '14px',
                                    paddingBlock: '5px',
                                    paddingInline: '12px',
                                },
                            }}
                            value={itemSource}
                            onChange={handleSourceChange}
                        >
                            <MenuItem
                                key={'Drawing'}
                                value={'Drawing'}
                            >
                                {t('From drawing')}
                            </MenuItem>
                            <MenuItem
                                key={'Manual'}
                                value={'Manual'}
                            >
                                {t('Manual input')}
                            </MenuItem>
                        </Select>
                    </FormControl>
                </TbxTooltip>

                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <NumericFormat
                        allowNegative={false}
                        customInput={TextField}
                        decimalSeparator={currencyFormat.decimal}
                        disabled={itemSource === 'Drawing' || isPartFromLibrary}
                        error={maxLengthError}
                        label={t('Longest fold')}
                        placeholder={`0 ${units}`}
                        size="small"
                        slotProps={{
                            inputLabel: {
                                shrink: true,
                            },
                            input: {
                                style: { fontSize: '14px' },
                            },
                            htmlInput: {
                                style: { textAlign: 'right', paddingBlock: '6px', paddingInline: '12px' },
                                'data-testid': 'order-item-longest-fold',
                            },
                        }}
                        suffix={` ${units}`}
                        sx={classes.formControl_small}
                        thousandSeparator={currencyFormat.group}
                        value={itemMaxLength}
                        variant="outlined"
                        onValueChange={handleMaxLengthChange}
                    />
                </TbxTooltip>

                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <NumericFormat
                        allowNegative={false}
                        customInput={TextField}
                        decimalScale={0}
                        decimalSeparator={currencyFormat.decimal}
                        disabled={itemSource === 'Drawing' || isPartFromLibrary}
                        error={numberOfFoldsError}
                        label={t('N° of folds')}
                        placeholder="0"
                        size="small"
                        slotProps={{
                            inputLabel: {
                                shrink: true,
                            },
                            input: {
                                style: { fontSize: '14px' },
                            },
                            htmlInput: {
                                style: { textAlign: 'right', paddingBlock: '6px', paddingInline: '12px' },
                                'data-testid': 'order-item-number-of-folds',
                            },
                        }}
                        sx={classes.formControl_small}
                        thousandSeparator={currencyFormat.group}
                        value={itemNumberOfFolds}
                        variant="outlined"
                        onValueChange={handleNumberOfFoldsChange}
                    />
                </TbxTooltip>
            </Box>
        </Box>
    )
}

ItemFolding.propTypes = {
    orderItemId: PropTypes.string,
}

export default memo(ItemFolding)
